import { IndexedDbNames } from '../../services/SystemNames';
import PilotAssignmentIdbRepository from './PilotAssignmentIdbRepository';

const indexedDbVersion = 8;

const BaseIdbRepository = {
    retryCount: 0,
    maxRetries: 3,
    retryDelay: 1000, //1 second

    async isDatabaseValidAsync() {
        return await this.executeWithRetry(async () => {
            let result = true;
            let db = await this.promiseRequestAsync(this.getDatabase());

            for (const prop in IndexedDbNames) {
                if (IndexedDbNames[prop] === IndexedDbNames.Database) continue;
                if (!db.objectStoreNames.contains(IndexedDbNames[prop])) {
                    result = false;
                    break;
                }
            }

            db.close();

            return result;
        });
    },

    getDatabase() {
        return window.indexedDB.open(IndexedDbNames.Database, indexedDbVersion);
    },

    async deleteDatabaseAsync() {
        await this.executeWithRetry(async () => {
            await this.promiseRequestAsync(window.indexedDB.deleteDatabase(IndexedDbNames.Database));
        });
    },

    promiseRequestAsync(request) {
        return new Promise((resolve, reject) => {
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
            request.onblocked = () => reject(new Error('Database connection blocked'));
        });
    },

    async executeWithRetry(operation) {
        for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
            try {
                this.retryCount = attempt;
                const result = await operation();
                this.retryCount = 0;
                return result;
            } catch (error) {
                if (this.isConnectionError(error) && attempt < this.maxTries) {
                    console.warn(`IndexedDB operation failed (attempt ${attempt + 1}/${this.maxTries + 1}): `, error.message);
                    await this.delay(this.retryDelay * (attempt + 1));
                    continue;
                }

                if (attempt === this.maxRetries) {
                    console.error('IndexedDB operation failed after all retries', error);
                    this.handleConnectionLost();
                }

                throw error;
            }
        }
    },

    isConnectionError(error) {
        const connectionErrorMessages = [
            'UnknownError',
            'Connection to Indexed Database server lost',
            'Database connection blocked',
            'InvalidStateError',
            'TransactionInactiveError'
        ];

        return connectionErrorMessages.some(msg =>
            error.message.includes(msg) || error.name.includes(msg)
        );
    },

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    },

    handleConnectionLost() {
        const event = new CustomEvent('indexeddb-connection-lost', {
            detail: {
                message: 'IndexedDB connection lost. Please refresh the page or try again.',
                retryCount: this.retryCount
            }
        });
        window.dispatchEvent(event);
    },

    init() {

        let db = null;
        let dbPromise = this.getDatabase();
        let objectStore = null;

        dbPromise.onerror = ('error', (err) => {
            console.warn('IndexedDB initialization error: ', err);
            if (this.isConnectionError(err)) {
                this.handleConnectionLost();
            }
        });

        dbPromise.onsuccess = (ev) => {
            db = ev.target.result;

            db.onversionchange = () => {
                db.close();
                console.warn('IndexedDB version changed, database closed');
            };

            db.onerror = (error) => {
                console.error('IndexedDB error: ', error);
                if (this.isConnectionError(error)) {
                    this.handleConnectionLost();
                }
            }
        };

        // Every time you add a new store, or make changed to an existing store,
        // you have to up the version number in the function getDatabase()
        dbPromise.onupgradeneeded = async (ev) => {
            db = ev.target.result;

            if (!db.objectStoreNames.contains(IndexedDbNames.MetaStore)) {
                db.createObjectStore(IndexedDbNames.MetaStore, {
                    keyPath: 'id'
                });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.LocationStore)) {
                objectStore = db.createObjectStore(IndexedDbNames.LocationStore, {
                    keyPath: 'locationId'
                });

                objectStore.createIndex('nameIDX', 'name', { unique: false });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.PilotagePilotStore)) {
                db.createObjectStore(IndexedDbNames.PilotagePilotStore, {
                    keyPath: 'pilotagePilotId'
                });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.PilotageStore)) {
                db.createObjectStore(IndexedDbNames.PilotageStore, {
                    keyPath: 'pilotageId'
                });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.SelectedPilotStationStore)) {
                db.createObjectStore(IndexedDbNames.SelectedPilotStationStore, {
                    keyPath: 'id'
                });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.DepartmentStore)) {
                objectStore = db.createObjectStore(IndexedDbNames.DepartmentStore, {
                    keyPath: 'departmentID'
                });

                objectStore.createIndex('displayNameIDX', 'displayName', { unique: false });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.EvaluationStore)) {
                db.createObjectStore(IndexedDbNames.EvaluationStore, {
                    keyPath: 'id'
                });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.PilotBoardingTypeStore)) {
                db.createObjectStore(IndexedDbNames.PilotBoardingTypeStore, {
                    keyPath: 'pilotBoardingTypeId'
                });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.PilotagePilotTypeStore)) {
                db.createObjectStore(IndexedDbNames.PilotagePilotTypeStore, {
                    keyPath: 'pilotagePilotTypeId'
                });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.ManeuverResponsibleTypeStore)) {
                db.createObjectStore(IndexedDbNames.ManeuverResponsibleTypeStore, {
                    keyPath: 'maneuverResponsibleTypeId'
                });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.HourCompensationTypeStore)) {
                db.createObjectStore(IndexedDbNames.HourCompensationTypeStore, {
                    keyPath: 'pilotagePilotHourCompensationTypeId'
                });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.VariableCompensationOverrideReasonTypeStore)) {
                db.createObjectStore(IndexedDbNames.VariableCompensationOverrideReasonTypeStore, {
                    keyPath: 'pilotagePilotVariableCompensationOverrideReasonTypeId'
                });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.PilotagePilotWaitingHoursReasonTypeStore)) {
                db.createObjectStore(IndexedDbNames.PilotagePilotWaitingHoursReasonTypeStore, {
                    keyPath: 'pilotagePilotWaitingHoursReasonTypeId'
                });
            }

            if (!db.objectStoreNames.contains(IndexedDbNames.PilotagePilotOvertimeActivityTypeStore)) {
                db.createObjectStore(IndexedDbNames.PilotagePilotOvertimeActivityTypeStore, {
                    keyPath: 'pilotagePilotOvertimeActivityTypeId'
                });
            }

            await PilotAssignmentIdbRepository.deleteAllAsync();

            // Lazy import inside async function
            const { IdbRepository: EvaluationIdbRepository } = await import('@pilot-web/pec-exam-evaluation');
            await EvaluationIdbRepository.deleteAllAsync();
        };
    },

    async setAsync(storeName, key, obj) {
        return await this.executeWithRetry(async () => {
            let result = await this.getAsync(storeName, key);

            if (result === undefined) {
                await this.addAsync(storeName, obj);
            } else {
                await this.putAsync(storeName, obj);
            }
        });
    },

    async addAsync(storeName, obj) {
        return await this.executeWithRetry(async () => {
            let db = await this.promiseRequestAsync(this.getDatabase());

            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                return;
            }

            let tx = this.makeTx(db, storeName, 'readwrite');
            let store = tx.objectStore(storeName);

            await this.promiseRequestAsync(store.add(obj))
                .catch((err) => {
                    console.warn('IndexedDB add error: ', err);
                    throw err;
                });

            db.close();
        });
    },

    async putAsync(storeName, obj) {
        return await this.executeWithRetry(async () => {
            let db = await this.promiseRequestAsync(this.getDatabase());

            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                return;
            }

            let tx = this.makeTx(db, storeName, 'readwrite');
            let store = tx.objectStore(storeName);

            await this.promiseRequestAsync(store.put(obj))
                .catch((err) => {
                    console.warn('IndexedDB put error: ', err);
                    throw err;
                });

            db.close();
        });
    },

    async deleteAsync(storeName, key) {
        return await this.executeWithRetry(async () => {
            let db = await this.promiseRequestAsync(this.getDatabase());

            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                return;
            }

            let tx = this.makeTx(db, storeName, 'readwrite');
            let store = tx.objectStore(storeName);

            await this.promiseRequestAsync(store.delete(key))
                .catch((err) => {
                    console.warn('IndexedDB delete error: ', err);
                    throw err;
                });

            db.close();
        });
    },

    async getAllAsync(storeName) {
        return await this.executeWithRetry(async () => {
            let result = null;
            let db = await this.promiseRequestAsync(this.getDatabase());

            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                return result;
            }

            let tx = this.makeTx(db, storeName, 'readonly');
            let store = tx.objectStore(storeName);

            await this.promiseRequestAsync(store.getAll())
                .then((res) => { result = res; })
                .catch((err) => {
                    console.warn('IndexedDB getAll error: ', err);
                    throw err;
                });

            db.close();

            return result;
        });
    },

    async getAsync(storeName, key) {
        return await this.executeWithRetry(async () => {
            let result = null;
            let db = await this.promiseRequestAsync(this.getDatabase());

            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                return result;
            }

            let tx = this.makeTx(db, storeName, 'readonly');
            let store = tx.objectStore(storeName);

            await this.promiseRequestAsync(store.get(key))
                .then((res) => { result = res; })
                .catch((err) => {
                    console.warn('IndexedDB get error: ', err);
                    throw err;
                });

            db.close();

            return result;
        });
    },

    async clearAsync(storeName) {
        return await this.executeWithRetry(async () => {
            let result = null;
            let db = await this.promiseRequestAsync(this.getDatabase());

            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                return result;
            }

            let tx = this.makeTx(db, storeName, 'readwrite');
            let store = tx.objectStore(storeName);

            await this.promiseRequestAsync(store.clear())
                .then((res) => { result = res; })
                .catch((err) => {
                    console.warn('IndexedDB clear error: ', err);
                    throw err;
                });

            db.close();

            return result;
        });
    },

    makeTx(db, storeName, mode) {
        let tx = db.transaction(storeName, mode);
        tx.onerror = (err) => {
            console.warn('Transaction error: ', err);
            if (this.isConnectionError(err)) {
                this.handleConnectionLost();
            }
        }

        return tx;
    },
};

export default BaseIdbRepository;
